import { useState, useEffect, useRef, useCallback } from 'react';
import { salesOrdersAPI, clientsAPI, filesAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, Eye, Edit, Download, Printer, Upload } from 'lucide-react';
import { toast } from 'sonner';

const SalesOrders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const printRef = useRef(null);
  const poFileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    order_number: '',
    client_id: '',
    client_name: '',
    client_address: '',
    client_gst: '',
    project_id: '',
    project_name: '',
    po_number: '',
    po_document_url: '',
    send_email_to: '',
    items: [],
    subtotal: 0,
    currency: 'INR',
    status: 'draft',
    notes: '',
  });

  const isAccounts = user?.role === 'accounts';  // Admin cannot change status
  const isSigningAuthority = user?.email === 'mansi@creativekhichdi.com' || user?.email === 'harshraj@creativekhichdi.com';

  // Status workflow options based on role
  const getStatusOptions = (currentStatus) => {
    if (isAccounts) {
      // Accounts can change any status (but not admin)
      return ['draft', 'approved', 'invoice_raised', 'rejected', 'cancelled', 'sent_for_revision'];
    }
    if (isSigningAuthority) {
      // Signing authority can approve/reject from draft
      if (currentStatus === 'draft' || currentStatus === 'sent_for_revision') {
        return ['draft', 'approved', 'rejected', 'sent_for_revision'];
      }
      return [currentStatus]; // Can't change other statuses
    }
    // Regular managers and admins can only create drafts
    return ['draft'];
  };

  // Format status for display
  const formatStatus = (status) => {
    if (status === 'invoice_raised') return 'Invoice Raised';
    if (status === 'sent_for_revision') return 'Sent for Revision';
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const fetchOrders = useCallback(async () => {
    try {
      const response = await salesOrdersAPI.getAll();
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch sales orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchClients();
  }, [fetchOrders, fetchClients]);

  const fetchNextSONumber = async () => {
    try {
      const response = await salesOrdersAPI.getNextNumber();
      return response.data.order_number;
    } catch (error) {
      console.error('Failed to fetch next SO number');
      return 'SO-0001';
    }
  };

  const handleOpenDialog = async () => {
    const nextNumber = await fetchNextSONumber();
    setFormData({
      ...formData,
      order_number: nextNumber,
    });
    setDialogOpen(true);
  };

  const handleClientChange = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      // Fetch ALL projects for this client
      try {
        const response = await clientsAPI.getProjects(clientId);
        setClientProjects(response.data);
      } catch (error) {
        console.error('Failed to fetch client projects');
        setClientProjects([]);
      }
      
      setFormData({
        ...formData,
        client_id: clientId,
        client_name: client.name,
        client_address: client.cost_center_address || '',
        client_gst: client.gst_number || '',
        currency: client.default_currency || 'INR',
        project_id: '',
        project_name: '',
        items: [],
        subtotal: 0,
      });
    } else {
      setClientProjects([]);
      setFormData({
        ...formData,
        client_id: '',
        client_name: '',
        client_address: '',
        client_gst: '',
        project_id: '',
        project_name: '',
        items: [],
        subtotal: 0,
      });
    }
  };

  // When address is selected, auto-populate GST
  const handleAddressSelect = (address) => {
    const client = clients.find(c => c.id === formData.client_id);
    setFormData({
      ...formData,
      client_address: address,
      client_gst: client?.gst_number || '',
    });
  };

  // Handle PO document upload
  const openPODocument = async (filename) => {
    const win = window.open('', '_blank');
    try {
      const res = await filesAPI.downloadPO(filename);
      const url = URL.createObjectURL(res.data);
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      win?.close();
      toast.error(error.response?.status === 404 ? 'PO document not found. Please upload it again.' : 'Failed to open PO document');
    }
  };

  const handlePOUpload = async (e, orderId = null) => {
    const file = e.target.files[0];
    if (file) {
      if (orderId) {
        // If we have an order ID, upload directly to server
        try {
          const response = await salesOrdersAPI.uploadPODocument(orderId, file);
          setFormData({
            ...formData,
            po_document_url: response.data.filename,
          });
          toast.success(`PO document "${response.data.original_filename}" uploaded successfully`);
        } catch (error) {
          toast.error('Failed to upload PO document');
        }
      } else {
        // For new orders, store the file reference temporarily
        setFormData({
          ...formData,
          po_document_url: file.name,
          _po_file: file, // Store file for upload after order creation
        });
        toast.success(`PO document "${file.name}" attached`);
      }
    }
  };

  const handleProjectChange = (projectId) => {
    const project = clientProjects.find(p => p.id === projectId);
    if (project) {
      // Map project particulars to sales order items
      const items = (project.particulars || []).map((item, i) => ({
        _rid: `proj-${projectId}-${i}`,
        description: `${item.item_name}${item.item_description ? ' - ' + item.item_description : ''}`,
        quantity: item.quantity,
        rate: item.rate,
        amount: item.quantity * item.rate,
      }));
      
      const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
      
      setFormData({
        ...formData,
        project_id: projectId,
        project_name: project.name,
        items: items,
        subtotal: subtotal,
        currency: project.currency || formData.currency,
      });
    } else {
      setFormData({
        ...formData,
        project_id: '',
        project_name: '',
        items: [],
        subtotal: 0,
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      newItems[index].amount = quantity * rate;
    }
    
    const subtotal = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    setFormData({ ...formData, items: newItems, subtotal });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { _rid: `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: '', quantity: 1, rate: 0, amount: 0 }],
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 0) {
      const newItems = formData.items.filter((_, i) => i !== index);
      const subtotal = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      setFormData({ ...formData, items: newItems, subtotal });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      const { _po_file, ...data } = formData;
      if (_po_file) data.po_document_url = '';

      const response = await salesOrdersAPI.create(data);
      toast.success('Sales order created successfully');
      if (_po_file) {
        try {
          await salesOrdersAPI.uploadPODocument(response.data.id, _po_file);
        } catch (uploadError) {
          toast.error('Order saved, but the PO document failed to upload. Please upload it from Edit.');
        }
      }
      
      setDialogOpen(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object') {
        toast.error(detail.msg || detail.message || 'Operation failed');
      } else {
        toast.error(detail || 'Operation failed');
      }
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      order_number: order.order_number,
      client_id: order.client_id || '',
      client_name: order.client_name || '',
      client_address: order.client_address || '',
      client_gst: order.client_gst || '',
      project_id: order.project_id || '',
      project_name: order.project_name || '',
      po_number: order.po_number || '',
      po_document_url: order.po_document_url || '',
      send_email_to: order.send_email_to || '',
      items: (order.items || []).map((it, i) => ({ ...it, _rid: it._rid || `existing-${order.id}-${i}` })),
      subtotal: order.subtotal || 0,
      currency: order.currency || 'INR',
      status: order.status || 'draft',
      notes: order.notes || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const updateData = {
        status: formData.status,
        notes: formData.notes,
        items: formData.items,
        subtotal: formData.subtotal,
        po_number: formData.po_number,
        po_document_url: formData.po_document_url,
        send_email_to: formData.send_email_to,
      };
      
      await salesOrdersAPI.update(editingOrder.id, updateData);
      toast.success('Sales order updated successfully');
      
      setEditDialogOpen(false);
      setEditingOrder(null);
      resetForm();
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object') {
        toast.error(detail.msg || detail.message || 'Update failed');
      } else {
        toast.error(detail || 'Update failed');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sales order?')) return;
    
    try {
      await salesOrdersAPI.delete(id);
      toast.success('Sales order deleted successfully');
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Validation error').join(', '));
      } else if (typeof detail === 'object') {
        toast.error(detail.msg || detail.message || 'Failed to delete sales order');
      } else {
        toast.error(detail || 'Failed to delete sales order');
      }
    }
  };

  const handleView = (order) => {
    setViewingOrder(order);
    setViewDialogOpen(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    const doc = printWindow.document;
    doc.open();
    
    const html = doc.createElement('html');
    const head = doc.createElement('head');
    const title = doc.createElement('title');
    title.textContent = `Sales Order - ${viewingOrder?.order_number || ''}`;
    const style = doc.createElement('style');
    style.textContent = `
      body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
      .header { text-align: center; margin-bottom: 30px; }
      .header h1 { margin: 0; font-size: 28px; }
      .header p { margin: 5px 0; color: #666; }
      .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
      .details-section h3 { margin: 0 0 10px 0; font-size: 14px; color: #666; }
      .details-section p { margin: 5px 0; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
      th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
      th { background-color: #f5f5f5; }
      .text-right { text-align: right; }
      .totals { width: 300px; margin-left: auto; }
      .totals .row { display: flex; justify-content: space-between; padding: 8px 0; }
      .totals .total-row { border-top: 2px solid #000; font-weight: bold; font-size: 18px; }
      .signatures { margin-top: 60px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 40px; }
      .signature-box { text-align: center; }
      .signature-line { border-top: 1px solid #000; margin-top: 60px; padding-top: 10px; }
      .notes { margin-top: 30px; padding: 15px; background: #f9f9f9; border-radius: 5px; }
      @media print { body { padding: 0; } }
    `;
    head.appendChild(title);
    head.appendChild(style);
    html.appendChild(head);
    
    const body = doc.createElement('body');
    body.innerHTML = printContent.innerHTML;
    html.appendChild(body);
    
    doc.appendChild(html);
    doc.close();
    printWindow.print();
  };

  const handleDownloadPDF = () => {
    // Use print to PDF functionality
    handlePrint();
    toast.info('Use "Save as PDF" in the print dialog to download');
  };

  const resetForm = () => {
    setFormData({
      order_number: '',
      client_id: '',
      client_name: '',
      client_address: '',
      client_gst: '',
      project_id: '',
      project_name: '',
      po_number: '',
      po_document_url: '',
      send_email_to: '',
      items: [],
      subtotal: 0,
      currency: 'INR',
      status: 'draft',
      notes: '',
    });
    setClientProjects([]);
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleEditDialogClose = (open) => {
    setEditDialogOpen(open);
    if (!open) {
      setEditingOrder(null);
      resetForm();
    }
  };

  // Get unique addresses from selected client
  const getClientAddresses = () => {
    const selectedClient = clients.find(c => c.id === formData.client_id);
    if (!selectedClient) return [];
    
    const addresses = [];
    if (selectedClient.cost_center_address) {
      addresses.push(selectedClient.cost_center_address);
    }
    return addresses;
  };

  if (loading) {
    return <div className="text-center py-12">Loading sales orders...</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="sales-orders-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sales Orders
          </h1>
          <p className="text-muted-foreground mt-2">Create and manage sales orders</p>
        </div>
        <Button className="flex items-center gap-2" onClick={handleOpenDialog} data-testid="create-order-button">
          <Plus className="h-4 w-4" />
          New Sales Order
        </Button>
      </div>

      {/* Create Sales Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle>Create Sales Order</DialogTitle>
            <DialogDescription>Create a sales order for a project</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* SO Number (Auto-generated) */}
            <div className="space-y-2">
              <Label htmlFor="order-number">Sales Order Number</Label>
              <Input
                id="order-number"
                value={formData.order_number}
                readOnly
                className="bg-muted"
                data-testid="order-number-input"
              />
              <p className="text-xs text-muted-foreground">Auto-generated</p>
            </div>

            {/* Client Dropdown */}
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <select
                id="client"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.client_id}
                onChange={(e) => handleClientChange(e.target.value)}
                required
                data-testid="order-client-select"
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Project Dropdown - All projects for client */}
            {formData.client_id && (
              <div className="space-y-2">
                <Label htmlFor="project">Project *</Label>
                <select
                  id="project"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.project_id}
                  onChange={(e) => handleProjectChange(e.target.value)}
                  required
                  data-testid="order-project-select"
                >
                  <option value="">Select a project</option>
                  {clientProjects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} ({project.status})
                    </option>
                  ))}
                </select>
                {clientProjects.length === 0 && (
                  <p className="text-xs text-amber-600">No projects found for this client.</p>
                )}
              </div>
            )}

            {/* Address Dropdown */}
            {formData.client_id && (
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <select
                  id="address"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.client_address}
                  onChange={(e) => handleAddressSelect(e.target.value)}
                  data-testid="order-address-select"
                >
                  <option value="">Select address</option>
                  {getClientAddresses().map((addr, idx) => (
                    <option key={`addr-${idx}-${addr.slice(0, 12)}`} value={addr}>{addr}</option>
                  ))}
                </select>
                <Textarea
                  value={formData.client_address}
                  onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                  placeholder="Or enter custom address"
                  rows={2}
                  className="mt-2"
                />
              </div>
            )}

            {/* Client GST (Auto-populated, Read-only when address selected) */}
            {formData.client_id && (
              <div className="space-y-2">
                <Label>GST Number</Label>
                <Input
                  value={formData.client_gst}
                  readOnly
                  className="bg-muted"
                  placeholder="GST auto-populated from client"
                  data-testid="order-gst-input"
                />
                <p className="text-xs text-muted-foreground">Auto-populated from client profile</p>
              </div>
            )}

            {/* PO Number (Optional) */}
            {formData.client_id && (
              <div className="space-y-2">
                <Label htmlFor="po-number">PO Number (Optional)</Label>
                <Input
                  id="po-number"
                  value={formData.po_number}
                  onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                  placeholder="Enter PO number if available"
                  data-testid="order-po-number-input"
                />
              </div>
            )}

            {/* PO Document Upload (Optional) */}
            {formData.client_id && (
              <div className="space-y-2">
                <Label>PO Document (Optional)</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="file"
                    ref={poFileInputRef}
                    onChange={handlePOUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    id="po-file-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => poFileInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload PO Document
                  </Button>
                  {formData.po_document_url && (
                    <span className="text-sm text-muted-foreground">{formData.po_document_url}</span>
                  )}
                </div>
              </div>
            )}

            {/* Send Email To (Optional) */}
            {formData.client_id && (
              <div className="space-y-2">
                <Label htmlFor="send-email-to">Send Email To (Optional)</Label>
                <Input
                  id="send-email-to"
                  value={formData.send_email_to}
                  onChange={(e) => setFormData({ ...formData, send_email_to: e.target.value })}
                  placeholder="Enter email addresses separated by commas"
                  data-testid="order-send-email-input"
                />
                <p className="text-xs text-muted-foreground">Multiple emails can be separated by commas</p>
              </div>
            )}

            {/* Particulars (Line Items from Project) */}
            {formData.project_id && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Particulars</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>
                
                {formData.items.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 text-sm font-medium">Description</th>
                          <th className="text-right p-2 text-sm font-medium w-20">Qty</th>
                          <th className="text-right p-2 text-sm font-medium w-28">Rate</th>
                          <th className="text-right p-2 text-sm font-medium w-28">Amount</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => (
                          <tr key={item._rid || `create-item-${index}`} className="border-t">
                            <td className="p-2">
                              <Input
                                value={item.description}
                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                placeholder="Description"
                                className="h-8"
                                required
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                className="h-8 text-right"
                                min="0"
                                required
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                value={item.rate}
                                onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                className="h-8 text-right"
                                min="0"
                                step="0.01"
                                required
                              />
                            </td>
                            <td className="p-2 text-right font-medium">
                              {formData.currency} {item.amount.toFixed(2)}
                            </td>
                            <td className="p-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(index)}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
                    No items. Items will be auto-populated when you select a project with particulars.
                  </p>
                )}
              </div>
            )}

            {/* Totals - Removed Tax as per requirement */}
            {formData.items.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Subtotal</Label>
                  <Input type="number" value={formData.subtotal.toFixed(2)} readOnly className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>Total</Label>
                  <Input
                    type="number"
                    value={formData.subtotal.toFixed(2)}
                    readOnly
                    className="bg-muted font-bold"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="order-submit-button">
                Create Sales Order
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Sales Order Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={handleEditDialogClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-dialog">
          <DialogHeader>
            <DialogTitle>Edit Sales Order - {editingOrder?.order_number}</DialogTitle>
            <DialogDescription>Update the sales order details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client</Label>
                <Input value={formData.client_name} readOnly className="bg-muted" />
              </div>
              <div>
                <Label>Project</Label>
                <Input value={formData.project_name} readOnly className="bg-muted" />
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status</Label>
              <select
                id="edit-status"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {getStatusOptions(editingOrder?.status || 'draft').map((status) => (
                  <option key={status} value={status}>
                    {formatStatus(status)}
                  </option>
                ))}
              </select>
              {!isAccounts && !isSigningAuthority && (
                <p className="text-xs text-muted-foreground">Only signing authorities or accounts can change status</p>
              )}
            </div>

            {/* PO Number */}
            <div className="space-y-2">
              <Label htmlFor="edit-po-number">PO Number (Optional)</Label>
              <Input
                id="edit-po-number"
                value={formData.po_number}
                onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                placeholder="Enter PO number"
              />
            </div>

            {/* PO Document */}
            <div className="space-y-2">
              <Label>PO Document (Optional)</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="file"
                  onChange={(e) => handlePOUpload(e, editingOrder?.id)}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  id="edit-po-file-upload"
                  data-testid="edit-po-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('edit-po-file-upload')?.click()}
                  className="flex items-center gap-2"
                  data-testid="edit-po-upload-btn"
                >
                  <Upload className="h-4 w-4" />
                  Upload PO Document
                </Button>
                {formData.po_document_url && (
                  <Button type="button" variant="link" className="px-1" onClick={() => openPODocument(formData.po_document_url)} data-testid="view-po-document-btn">
                    View PO document
                  </Button>
                )}
              </div>
            </div>

            {/* Send Email To */}
            <div className="space-y-2">
              <Label htmlFor="edit-send-email-to">Send Email To (Optional)</Label>
              <Input
                id="edit-send-email-to"
                value={formData.send_email_to}
                onChange={(e) => setFormData({ ...formData, send_email_to: e.target.value })}
                placeholder="Enter email addresses separated by commas"
              />
            </div>

            {/* Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Particulars</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
              
              {formData.items.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-2 text-sm font-medium">Description</th>
                        <th className="text-right p-2 text-sm font-medium w-20">Qty</th>
                        <th className="text-right p-2 text-sm font-medium w-28">Rate</th>
                        <th className="text-right p-2 text-sm font-medium w-28">Amount</th>
                        <th className="w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={item._rid || `edit-item-${index}`} className="border-t">
                          <td className="p-2">
                            <Input
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              placeholder="Description"
                              className="h-8"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              className="h-8 text-right"
                              min="0"
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                              className="h-8 text-right"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="p-2 text-right font-medium">
                            {formData.currency} {(item.amount || 0).toFixed(2)}
                          </td>
                          <td className="p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Totals - Removed Tax */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Subtotal</Label>
                <Input value={formData.subtotal.toFixed(2)} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <Input
                  value={formData.subtotal.toFixed(2)}
                  readOnly
                  className="bg-muted font-bold"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => handleEditDialogClose(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="edit-order-submit-button">
                Update Sales Order
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Orders List */}
      {orders.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              No sales orders yet. Create a sales order to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {orders.map((order) => (
            <Card key={order.id} className="hover:shadow-md transition-shadow" data-testid={`order-card-${order.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl font-semibold">{order.order_number}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {order.client_name} - {order.project_name}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(order)}
                      title="View"
                      data-testid={`view-order-${order.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(order)}
                      title="Edit"
                      data-testid={`edit-order-${order.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {/* Only accounts/admin can delete */}
                    {isAccounts && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(order.id)}
                        title="Delete"
                        data-testid={`delete-order-${order.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Status</p>
                    <span
                      className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
                        order.status === 'invoice_raised'
                          ? 'bg-green-100 text-green-700'
                          : order.status === 'approved'
                          ? 'bg-blue-100 text-blue-700'
                          : order.status === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : order.status === 'cancelled'
                          ? 'bg-gray-100 text-gray-700'
                          : order.status === 'sent_for_revision'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {formatStatus(order.status)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Total</p>
                    <p className="text-sm font-bold mt-1">{order.currency} {order.total?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Created By</p>
                    <p className="text-sm font-medium mt-1">{order.created_by_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Created On</p>
                    <p className="text-sm font-medium mt-1">
                      {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">PO Number</p>
                    <p className="text-sm font-medium mt-1">{order.po_number || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View/Print Sales Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Sales Order Preview</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {viewingOrder && (
            <div ref={printRef} className="p-6 bg-white">
              {/* Header */}
              <div className="header text-center border-b pb-6 mb-6">
                <div className="flex justify-center mb-4">
                  <img src="/company-logo.png" alt="Creative Khichdi" className="h-16 w-16 object-contain" />
                </div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  SALES ORDER
                </h1>
                <p className="text-xl mt-2 font-semibold">{viewingOrder.order_number}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Date: {new Date(viewingOrder.created_at).toLocaleDateString('en-IN')}
                </p>
                <p className="text-sm text-gray-500">
                  Created By: {viewingOrder.created_by_name || 'Unknown'}
                </p>
              </div>

              {/* Client & Project Details */}
              <div className="details-grid grid grid-cols-2 gap-8 mb-8">
                <div className="details-section">
                  <h3 className="font-semibold text-gray-600 mb-2 uppercase text-sm">Bill To</h3>
                  <p className="font-bold text-lg">{viewingOrder.client_name}</p>
                  <p className="text-sm whitespace-pre-line mt-1">{viewingOrder.client_address}</p>
                  {viewingOrder.client_gst && (
                    <p className="text-sm mt-2"><strong>GST:</strong> {viewingOrder.client_gst}</p>
                  )}
                </div>
                <div className="details-section text-right">
                  <h3 className="font-semibold text-gray-600 mb-2 uppercase text-sm">Project Details</h3>
                  <p className="font-bold text-lg">{viewingOrder.project_name}</p>
                  {viewingOrder.po_number && (
                    <p className="text-sm mt-1"><strong>PO Number:</strong> {viewingOrder.po_number}</p>
                  )}
                  <p className="text-sm mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      viewingOrder.status === 'invoice_raised'
                        ? 'bg-green-100 text-green-700'
                        : viewingOrder.status === 'approved'
                        ? 'bg-blue-100 text-blue-700'
                        : viewingOrder.status === 'rejected'
                        ? 'bg-red-100 text-red-700'
                        : viewingOrder.status === 'cancelled'
                        ? 'bg-gray-100 text-gray-700'
                        : viewingOrder.status === 'sent_for_revision'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {formatStatus(viewingOrder.status)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Items Table */}
              <div className="mb-8">
                <h3 className="font-semibold text-gray-600 mb-3 uppercase text-sm">Particulars</h3>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-left text-sm font-semibold">#</th>
                      <th className="border p-3 text-left text-sm font-semibold">Description</th>
                      <th className="border p-3 text-right text-sm font-semibold">Qty</th>
                      <th className="border p-3 text-right text-sm font-semibold">Rate</th>
                      <th className="border p-3 text-right text-sm font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingOrder.items?.map((item, index) => (
                      <tr key={`view-item-${index}-${item.description?.slice(0, 8) || ''}`}>
                        <td className="border p-3 text-sm">{index + 1}</td>
                        <td className="border p-3 text-sm">{item.description}</td>
                        <td className="border p-3 text-right text-sm">{item.quantity}</td>
                        <td className="border p-3 text-right text-sm">{viewingOrder.currency} {item.rate?.toFixed(2)}</td>
                        <td className="border p-3 text-right text-sm font-medium">{viewingOrder.currency} {item.amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals - Removed Tax */}
              <div className="totals flex justify-end mb-8">
                <div className="w-72">
                  <div className="row flex justify-between py-2 border-b">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">{viewingOrder.currency} {viewingOrder.subtotal?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="total-row flex justify-between py-3 border-t-2 border-black">
                    <span className="text-lg font-bold">Total:</span>
                    <span className="text-lg font-bold">{viewingOrder.currency} {viewingOrder.total?.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {viewingOrder.notes && (
                <div className="notes mb-8 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-600 mb-2 uppercase text-sm">Notes</h3>
                  <p className="text-sm whitespace-pre-line">{viewingOrder.notes}</p>
                </div>
              )}

              {/* Signing Authorities - Three signature fields */}
              <div className="signatures mt-16">
                <h3 className="font-semibold text-gray-600 mb-8 uppercase text-sm text-center">Authorized Signatures</h3>
                <div className="grid grid-cols-3 gap-8">
                  <div className="signature-box text-center">
                    <div className="h-20 border-b border-gray-400"></div>
                    <p className="mt-3 font-semibold">Manager</p>
                    <p className="text-xs text-gray-500">Date: _______________</p>
                  </div>
                  <div className="signature-box text-center">
                    <div className="h-20 border-b border-gray-400"></div>
                    <p className="mt-3 font-semibold">Accounts</p>
                    <p className="text-xs text-gray-500">Date: _______________</p>
                  </div>
                  <div className="signature-box text-center">
                    <div className="h-20 border-b border-gray-400"></div>
                    <p className="mt-3 font-semibold">Founders (M/H)</p>
                    <p className="text-xs text-gray-500">Date: _______________</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesOrders;
